import { getJSON } from 'common/services/api'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import type { DiscrepancyRecord } from '../types'
import {
  STOCK_DISCREPANCIES_QUERY_KEY,
  STOCK_DISCREPANCIES_URL
} from './queryConstants'

type StockDiscrepanciesResponse = {
  records: DiscrepancyRecord[]
  total: number
  skip: number
  limit: number
  hasMore: boolean
}

type CachedStockDiscrepancies = StockDiscrepanciesResponse & {
  // The filter (everything but the page) this response was fetched for
  filterKey: string
}

type UseStockDiscrepanciesProps = {
  startDate?: dayjs.Dayjs
  endDate?: dayjs.Dayjs
  status?: 'PENDING' | 'RECONCILED' | 'INVESTIGATING'
  skip?: number
  limit?: number
  residentId?: string[]
  enabled?: boolean
}

type UseStockDiscrepanciesReturn = {
  data: StockDiscrepanciesResponse | null
  isLoading: boolean
  isError: boolean
  // True while `data` is still the previous page's response
  isPreviousData: boolean
  refetch: () => void
}

export const useStockDiscrepancies = ({
  startDate,
  endDate,
  status,
  skip,
  limit,
  residentId,
  enabled = true
}: UseStockDiscrepanciesProps): UseStockDiscrepanciesReturn => {
  const formattedStartDate = startDate
    ? dayjs(startDate).startOf('day').toISOString()
    : dayjs().subtract(30, 'days').startOf('day').toISOString()

  const formattedEndDate = endDate
    ? dayjs(endDate).endOf('day').toISOString()
    : dayjs().endOf('day').toISOString()

  const params = getQueryParams({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    ...(status && { status }),
    ...(skip !== undefined && { skip: skip.toString() }),
    ...(limit !== undefined && { limit: limit.toString() }),
    ...(residentId && residentId.length > 0 && { residentId })
  })

  const filterKey = JSON.stringify([
    formattedStartDate,
    formattedEndDate,
    status,
    limit,
    residentId
  ])

  const { data, isLoading, isError, isPreviousData, refetch } =
    useQuery<CachedStockDiscrepancies>(
      [
        STOCK_DISCREPANCIES_QUERY_KEY,
        formattedStartDate,
        formattedEndDate,
        status,
        skip,
        limit,
        residentId
      ],
      async () => ({
        ...((await getJSON({
          url: `${STOCK_DISCREPANCIES_URL}?${params}`
        })) as StockDiscrepanciesResponse),
        filterKey
      }),
      {
        enabled,
        retry: 1,
        // Keeps the current page (and its total, which drives the pager) on
        // screen while the next one loads
        keepPreviousData: true
      }
    )

  // keepPreviousData holds on to the last response for any key change. That
  // suits a page change, but after a tab or filter change those rows belong to
  // a different filter, so withhold them until this filter's response arrives.
  const isOtherFilter = isPreviousData && data?.filterKey !== filterKey

  return {
    data: isOtherFilter ? null : (data ?? null),
    isLoading: isLoading || isOtherFilter,
    isError,
    isPreviousData: isPreviousData && !isOtherFilter,
    refetch
  }
}

export default useStockDiscrepancies
